import * as z from 'zod';

/**
 * System-user (internal staff identity) contracts — Noor Auth admin API
 * (`AuthService_*`: identities, users, sessions). Zod is the single source of
 * truth; every type below is inferred, never hand-written.
 */

/** Identity contour this surface manages. Only `user` is served for now. */
export const SYSTEM_USER_APP = 'user' as const;

/**
 * System roles (cpanel `USER_ROLES`, lowercase wire values). Used by the create
 * form and the role filter. The identity card itself keeps `role` a plain
 * string: a role newer than this build must not break the list.
 */
export const SYSTEM_USER_ROLES = [
  'admin',
  'manager',
  'b2b_back',
  'accountant',
  'backend',
  'ibox_manager',
  'b2b_front',
  'xolodilnik',
  'operator',
  'af_manager',
  'b2b_head',
  'translator',
  'finance',
  'af_admin',
  'call_center',
  'ops_admin',
  'af_call_center',
] as const;
export const systemUserRoleSchema = z.enum(SYSTEM_USER_ROLES);
export type SystemUserRole = z.infer<typeof systemUserRoleSchema>;

/** Identity statuses (`pending | active | rejected | banned | suspended | deleted`). */
export const SYSTEM_USER_STATUSES = [
  'pending',
  'active',
  'rejected',
  'banned',
  'suspended',
  'deleted',
] as const;
export const systemUserStatusSchema = z.enum(SYSTEM_USER_STATUSES);
export type SystemUserStatus = z.infer<typeof systemUserStatusSchema>;

/**
 * Status options offered by the list FILTER. An identity itself can be in
 * any of `SYSTEM_USER_STATUSES`, but the filter narrows to active/banned
 * (`all` is the select's empty value, not an option).
 */
export const SYSTEM_USER_STATUS_FILTERS = ['active', 'banned'] as const;
export type SystemUserStatusFilter = (typeof SYSTEM_USER_STATUS_FILTERS)[number];

/**
 * Search modes for `GET /api/v1/identities/{app}` (`search_type`).
 *
 * `name` is served only on the `user` contour — `client`, `courier` and
 * `merchant` answer 400 and are phone-only. This surface manages `user`
 * (see `SYSTEM_USER_APP`), so both modes are legal here.
 */
export const SYSTEM_USER_SEARCH_TYPES = ['name', 'phone'] as const;
export const systemUserSearchTypeSchema = z.enum(SYSTEM_USER_SEARCH_TYPES);
export type SystemUserSearchType = z.infer<typeof systemUserSearchTypeSchema>;

export const DEFAULT_SYSTEM_USER_SEARCH_TYPE: SystemUserSearchType = 'name';

export const SYSTEM_USER_SEARCH_TYPE_LABEL_KEYS: Record<string, string> = {
  name: 'users:list.searchTypes.name',
  phone: 'users:list.searchTypes.phone',
};

/** Staff details stored by auth; empty for identities created before names. */
export const systemUserDetailsSchema = z.object({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
});
export type SystemUserDetails = z.infer<typeof systemUserDetailsSchema>;

/**
 * Identity card (`v1GetIdentityResponse`, no `password_hash`). `created_by_*`
 * are empty both for self-registered identities and for ones created before
 * auth tracked provenance — the UI renders both as "unknown".
 */
export const systemUserSchema = z
  .object({
    id: z.string().min(1),
    phone: z.string().min(1),
    role: z.string().min(1),
    status: z.string().min(1),
    created_at: z.string().min(1),
    updated_at: z.string().min(1),
    created_by_type: z.string().nullish(),
    created_by_id: z.string().nullish(),
    user: systemUserDetailsSchema.nullish(),
  })
  .loose();
export type SystemUser = z.infer<typeof systemUserSchema>;

/** Gateway list envelope meta (`meta.total` counts every record under filters). */
export const systemUserListMetaSchema = z
  .object({
    total: z.number().int().nonnegative().nullish(),
    page: z.number().int().positive().nullish(),
    page_count: z.number().int().nonnegative().nullish(),
  })
  .loose();
export type SystemUserListMeta = z.infer<typeof systemUserListMetaSchema>;

/** Table-friendly list result: parsed rows plus the envelope meta. */
export const systemUserListSchema = z.object({
  list: z.array(systemUserSchema),
  meta: systemUserListMetaSchema.optional(),
});
export type SystemUserList = z.infer<typeof systemUserListSchema>;

/** Raw wire envelope accepted from the gateway (`{list, meta}`, `data` ignored). */
export const systemUserListEnvelopeSchema = z
  .object({
    list: z.array(z.unknown()).nullish(),
    data: z.unknown().optional(),
    meta: systemUserListMetaSchema.nullish(),
  })
  .loose();
export type SystemUserListEnvelope = z.infer<typeof systemUserListEnvelopeSchema>;

/** Single-identity envelope (`POST /users/{id}/identity` → `{data, meta}`). */
export const systemUserDetailEnvelopeSchema = z
  .object({
    data: systemUserSchema.nullish(),
  })
  .loose();
export type SystemUserDetailEnvelope = z.infer<typeof systemUserDetailEnvelopeSchema>;

/** Login session (`v1Session`); `revoked_*` are set only once revoked. */
export const systemUserSessionSchema = z
  .object({
    id: z.string().min(1),
    user_type: z.string().nullish(),
    user_id: z.string().nullish(),
    device_id: z.string().nullish(),
    device_name: z.string().nullish(),
    platform: z.string().nullish(),
    ip: z.string().nullish(),
    user_agent: z.string().nullish(),
    created_at: z.string().nullish(),
    last_seen_at: z.string().nullish(),
    expires_at: z.string().nullish(),
    revoked_at: z.string().nullish(),
    revoked_reason: z.string().nullish(),
  })
  .loose();
export type SystemUserSession = z.infer<typeof systemUserSessionSchema>;

export const systemUserSessionListSchema = z.object({
  list: z.array(systemUserSessionSchema),
  meta: systemUserListMetaSchema.optional(),
});
export type SystemUserSessionList = z.infer<typeof systemUserSessionListSchema>;

export const systemUserSessionListEnvelopeSchema = z
  .object({
    list: z.array(z.unknown()).nullish(),
    data: z.unknown().optional(),
    meta: systemUserListMetaSchema.nullish(),
  })
  .loose();
export type SystemUserSessionListEnvelope = z.infer<
  typeof systemUserSessionListEnvelopeSchema
>;

/** Create-staff payload (`v1CreateUserRequest`). */
export const createSystemUserPayloadSchema = z.object({
  phone: z.string().min(1),
  role: systemUserRoleSchema,
  first_name: z.string().min(1),
  last_name: z.string().min(1),
});
export type CreateSystemUserPayload = z.infer<typeof createSystemUserPayloadSchema>;

/**
 * Update-staff payload (`PATCH /api/v1/users/{user_id}`). Every field is
 * optional: the service only touches what is sent — a missing field, or one
 * arriving as an empty string, leaves the stored value unchanged (so a name
 * can never be cleared through this endpoint). Callers therefore send ONLY
 * the values that actually changed; an empty object means "nothing to do"
 * and must not be sent.
 */
export const updateSystemUserPayloadSchema = z.object({
  first_name: z.string().min(1).optional(),
  last_name: z.string().min(1).optional(),
  role: systemUserRoleSchema.optional(),
});
export type UpdateSystemUserPayload = z.infer<typeof updateSystemUserPayloadSchema>;

/**
 * Write-action envelope (`{data: {}, meta}` — ban/unban/delete/reset/create/
 * revoke). The body carries no payload: the password goes to the owner by SMS,
 * and state changes are observed by refetching. Parsed loosely so an empty or
 * `{data:{}}` body both pass.
 */
export const systemUserWriteEnvelopeSchema = z
  .object({
    data: z.unknown().optional(),
    meta: z.unknown().optional(),
  })
  .loose();
export type SystemUserWriteEnvelope = z.infer<typeof systemUserWriteEnvelopeSchema>;
